import { z } from 'zod';
import {
  PROPERTY_CATEGORIES,
  PropertyCategorySchema,
  PropertySubtypeSchema,
  SUBTYPE_TO_CATEGORY,
} from '../common/property-taxonomy.schema';
import { TransactionTypeSchema } from '../common/transaction-type.schema';
import { DOSSIER_NUMBER_STYLES, FACT_VALUE_TYPES } from './dossier.schemas';
import { PropertyFieldLinkKeySchema, PropertyFieldLinkOverrideSchema } from './linked-property-field-keys';

/**
 * The operator fact-key taxonomy surface (IMM-500.5). An admin management screen
 * over the platform vocabulary a property fact may be keyed by. The operator edits
 * a key's `label`, `scope`, `publicSafe`, Objekt-field mapping, group, object types
 * and need per product and object type; `key` and `valueType` are immutable and
 * deliberately absent from the update body. From IMM-1383.4 it also adds an entry and, from IMM-1383.5, retires one.
 */

export const FactScopeSchema = z.enum(['PROPERTY_INTRINSIC', 'DEAL_CONTEXT']);

export type FactScope = z.infer<typeof FactScopeSchema>;

export const FactValueTypeSchema = z.enum(FACT_VALUE_TYPES);

export type FactValueType = z.infer<typeof FactValueTypeSchema>;

/** The catalog's read-only description of an entry (IMM-1383), stored as `details jsonb`. */
export const FACT_KEY_ORIGINS = ['OAB_0_1', 'IMMOOS_ADDITION'] as const;

export const FactKeyDetailsSchema = z.object({
  description: z.string().nullable(),
  valueList: z.array(z.string()),
  subfields: z.array(
    z.object({
      name: z.string(),
      valueType: FactValueTypeSchema,
      unit: z.string().nullable(),
      valueList: z.array(z.string()),
      origin: z.enum(FACT_KEY_ORIGINS).optional(),
    }),
  ),
  sources: z.array(z.string()),
  photo: z.object({ derivable: z.enum(['YES', 'PARTLY', 'NO']), limit: z.string().nullable() }).nullable(),
  consequenceWhenMissing: z.string().nullable(),
  collectionHint: z.string().nullable(),
  ruleReferences: z.array(z.object({ reqId: z.string(), rule: z.string(), usage: z.string() })),
});

export type FactKeyDetails = z.infer<typeof FactKeyDetailsSchema>;

/** The ID prefix IS the group token: oab-0.1's 18 groups in oab order, then the two additions. */
export const FACT_KEY_GROUPS = [
  'AUF',
  'KAT',
  'LAG',
  'PLR',
  'ERS',
  'GB',
  'GEB',
  'FLA',
  'BAU',
  'ENE',
  'MOD',
  'ZUS',
  'NEB',
  'NUT',
  'WEG',
  'ZVG',
  'UNT',
  'SON',
  'VMK',
  'GEW',
] as const;

/**
 * The ID an operator may give a new entry (IMM-1383.4): an existing group prefix, then one or more
 * dot-separated segments of capitals, digits and underscores. Lower case marks a subfield, which an
 * operator cannot add. Applied by the domain (a coded refusal) and by the dialog — never by the body schema.
 */
export const NEW_FACT_KEY_ID_PATTERN = new RegExp(`^(${FACT_KEY_GROUPS.join('|')})(\\.[A-Z0-9_]+)+$`);

export const FACT_KEY_FREQUENCIES = [
  'ONCE',
  'LIST',
  'PER_BUILDING',
  'PER_UNIT',
  'PER_RIGHT',
  'PER_DOCUMENT',
  'PER_FINDING',
  'PER_PARCEL',
  'PER_MEASURE',
  'PER_PARTIAL_AREA',
  'PER_LAND_REGISTER_SHEET',
  'PER_INVENTORY_ENTRY',
  'PER_OWNER',
  'PER_TRANSITION',
  'PER_BENEFICIARY',
  'PER_ENCROACHMENT',
  'PER_BUILDING_ENCUMBRANCE',
] as const;

export const FACT_KEY_STATUSES = ['ACTIVE', 'RETIRED'] as const;
export const FACT_KEY_PRODUCTS = ['ACQUISITION_BRIEFING', 'VALUATION_1A', 'VALUATION_1B', 'EXPOSE'] as const;
/** A stored need, strongest first. `NOT_USED` is only ever stored with at least one condition (ADR 0069). */
export const FACT_KEY_NEEDS = ['REQUIRED', 'RECOMMENDED', 'OPTIONAL', 'NOT_USED'] as const;
export const NEED_SITUATIONS = ['LET', 'VACANT', 'NEW_BUILD'] as const;

export const FactKeyGroupSchema = z.enum(FACT_KEY_GROUPS);
export const FactKeyFrequencySchema = z.enum(FACT_KEY_FREQUENCIES);
export const FactKeyOriginSchema = z.enum(FACT_KEY_ORIGINS);
export const FactKeyStatusSchema = z.enum(FACT_KEY_STATUSES);
export const FactKeyProductSchema = z.enum(FACT_KEY_PRODUCTS);
export const FactKeyNeedSchema = z.enum(FACT_KEY_NEEDS);
export const NeedSituationSchema = z.enum(NEED_SITUATIONS);

export type FactKeyGroup = z.infer<typeof FactKeyGroupSchema>;
export type FactKeyFrequency = z.infer<typeof FactKeyFrequencySchema>;
export type FactKeyOrigin = z.infer<typeof FactKeyOriginSchema>;
export type FactKeyStatus = z.infer<typeof FactKeyStatusSchema>;
export type FactKeyProduct = z.infer<typeof FactKeyProductSchema>;
export type FactKeyNeed = z.infer<typeof FactKeyNeedSchema>;
export type NeedSituation = z.infer<typeof NeedSituationSchema>;

/** One need row: product and object type, optionally narrowed by marketing type, subtype and situation (`null` = any). */
export const FactKeyNeedCellSchema = z
  .object({
    product: FactKeyProductSchema,
    category: PropertyCategorySchema,
    marketingType: TransactionTypeSchema.nullable().default(null),
    subtype: PropertySubtypeSchema.nullable().default(null),
    situation: NeedSituationSchema.nullable().default(null),
    need: FactKeyNeedSchema,
  })
  .refine((cell) => cell.subtype === null || SUBTYPE_TO_CATEGORY[cell.subtype] === cell.category, {
    message: 'The subtype belongs to another object type',
    path: ['subtype'],
  });

export type FactKeyNeedCell = z.infer<typeof FactKeyNeedCellSchema>;

/** One row of the operator taxonomy screen. */
export const FactKeyViewSchema = z.object({
  key: z.string(),
  label: z.string(),
  valueType: FactValueTypeSchema,
  scope: FactScopeSchema,
  publicSafe: z.boolean(),
  propertyFieldKey: z.string().nullable(),
  propertyFieldKeyOverrides: z.array(PropertyFieldLinkOverrideSchema),
  updatedAt: z.iso.datetime(),
  groupId: FactKeyGroupSchema,
  frequency: FactKeyFrequencySchema,
  categories: z.array(z.enum(PROPERTY_CATEGORIES)).min(1),
  needs: z.array(FactKeyNeedCellSchema),
  origin: FactKeyOriginSchema,
  status: FactKeyStatusSchema,
  parentKey: z.string().nullable(),
  personalData: z.boolean(),
});

export type FactKeyView = z.infer<typeof FactKeyViewSchema>;

/** One entry with the read-only catalog details the list leaves out. */
export const FactKeyDetailViewSchema = FactKeyViewSchema.extend({
  numberStyle: z.enum(DOSSIER_NUMBER_STYLES).nullable(),
  unit: z.string().nullable(),
  details: FactKeyDetailsSchema.nullable(),
  /** Whether the entry is still in use (IMM-1383.5); feeds the „Stilllegen“ warnings. */
  usage: z.object({ filledByDocumentAnalysis: z.boolean() }),
});

export type FactKeyDetailView = z.infer<typeof FactKeyDetailViewSchema>;

export const ListFactKeysResponseSchema = z.object({
  data: z.array(FactKeyViewSchema),
});

export type ListFactKeysResponse = z.infer<typeof ListFactKeysResponseSchema>;

export const FactKeyPathParamsSchema = z.object({
  key: z.string(),
});

const uniqueCells = (
  cells: ReadonlyArray<{
    product: string;
    category: string;
    marketingType: string | null;
    subtype: string | null;
    situation: string | null;
  }>,
): boolean =>
  new Set(
    cells.map((cell) =>
      [cell.product, cell.category, cell.marketingType ?? '', cell.subtype ?? '', cell.situation ?? ''].join('|'),
    ),
  ).size === cells.length;

/**
 * The editable subset, every field optional so a patch touches only what it names.
 * `propertyFieldKey` when non-null must be a real `PropertyFieldLinkKey`; an
 * explicit `null` clears the mapping. `needs` replaces the whole matrix: omitted
 * leaves it alone, `[]` sets every cell to „Nicht genutzt“. `categories: []` and a
 * blank `label` deliberately reach the domain's coded `property.fact-key.incomplete`
 * refusal. `key`, `valueType`, `frequency` and `origin` are absent, so `.strict()`
 * rejects a request carrying them — they are immutable.
 */
export const UpdateFactKeyBodySchema = z
  .object({
    label: z.string().max(120).optional(),
    scope: FactScopeSchema.optional(),
    publicSafe: z.boolean().optional(),
    propertyFieldKey: PropertyFieldLinkKeySchema.nullable().optional(),
    groupId: FactKeyGroupSchema.optional(),
    categories: z.array(PropertyCategorySchema).optional(),
    needs: z.array(FactKeyNeedCellSchema).refine(uniqueCells, { message: 'Each need row appears once' }).optional(),
  })
  .strict();

export type UpdateFactKeyBody = z.infer<typeof UpdateFactKeyBodySchema>;

/**
 * A new operator entry (IMM-1383.4). The server sets `origin`, `status`, `publicSafe`,
 * `propertyFieldKey`, `parentKey`, `personalData`, `details` and `scope`, so `.strict()`
 * refuses a request carrying them. A blank `label`, `categories: []` and a malformed
 * `key` deliberately reach the domain's coded refusals.
 */
export const CreateFactKeyBodySchema = z
  .object({
    key: z.string().max(80),
    label: z.string().max(120),
    groupId: FactKeyGroupSchema,
    valueType: FactValueTypeSchema,
    frequency: FactKeyFrequencySchema,
    categories: z.array(PropertyCategorySchema),
    needs: z.array(FactKeyNeedCellSchema).refine(uniqueCells, { message: 'Each need row appears once' }).default([]),
    numberStyle: z.enum(DOSSIER_NUMBER_STYLES).nullable().default(null),
    unit: z.string().trim().max(32).nullable().default(null),
  })
  .strict();

export type CreateFactKeyBody = z.input<typeof CreateFactKeyBodySchema>;
